/* eslint-disable no-use-before-define */
/* eslint-disable no-underscore-dangle */
import React, { FC, useState } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { store } from 'redux/store';
import { State } from 'redux/initialState';
import Swal from 'sweetalert2';

import { setModal } from 'modules/_shared/redux/modalActions';
import { updateBeneficiary } from 'modules/beneficiaries/redux/beneficiaryActions';

import { Beneficiary } from 'types';
import { getActualSociety } from 'utils/filters';

import UnpauseBeneficiaryAllPlansSection from './components/UnpauseBeneficiaryAllPlansSection';
import UnpauseBeneficiaryDateSection from './components/UnpauseBeneficiaryDateSection';
import UnpauseBeneficiaryHeader from './components/UnpauseBeneficiaryHeader';
import UnpauseBeneficiaryFooter from './components/UnpauseBeneficiaryFooter';

import CustomLoading from '../../../_shared/components/CustomLoading';
import '../Modals.scss';

type UnpauseBeneficiaryModalProps = {
  beneficiaryId: string;
  beneficiary: Beneficiary;
};

const UnpauseBeneficiaryModal: FC<UnpauseBeneficiaryModalProps> = ({
  beneficiaryId,
  beneficiary,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const { actualSociety } = useSelector((state: State) => state?.society);
  const user = useSelector((state: State) => state?.user);

  const formatDateForInput = (date: string | Date | null): string => {
    if (!date) return '';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return format(dateObj, 'yyyy-MM-dd');
    } catch {
      return '';
    }
  };

  const initialEndDate = beneficiary?.pause?.endDate
    ? formatDateForInput(beneficiary.pause.endDate)
    : '';

  const [pauseEndDate, setPauseEndDate] = useState(initialEndDate);
  const [unPauseAllPlans, setUnPauseAllPlans] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const handleChangePauseEndDate = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPauseEndDate(event.target.value);
  };

  const handleChangeUnPauseAllPlans = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setUnPauseAllPlans(event.target.checked);
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  async function updateBeneficiaryData(
    id: string,
    beneficiaryData: Partial<Beneficiary>
  ) {
    try {
      await store.dispatch(
        updateBeneficiary({
          id,
          data: {
            ...beneficiaryData,
            societyId: actualSociety._id,
            userId: user._id,
          },
          showAlert: false,
        })
      );
    } catch (error) {
      console.log(error); // eslint-disable-line
    }
  }

  const handleUnpauseBeneficiary = async () => {
    try {
      setIsLoading(true);

      // Get existing pause data and add cancellation properties
      const existingPause = beneficiary?.pause;
      if (!existingPause) {
        throw new Error('Pause data not found');
      }

      const updatedPause = {
        ...existingPause,
        isCancelled: true,
        cancelledAt: new Date(),
        cancelledBy: user._id,
        // Update endDate if it was modified
        endDate: pauseEndDate
          ? (pauseEndDate as string | Date | null)
          : existingPause.endDate,
      };

      const unpauseData = {
        pause: updatedPause,
        isPaused: false,
        cascade: !!unPauseAllPlans,
      };

      await updateBeneficiaryData(beneficiaryId, unpauseData);
      getActualSociety(user, actualSociety._id);

      Swal.fire({
        icon: 'success',
        title: `<h4 class="nk-modal-title">${t(
          'SuccessfullyUnpausedBeneficiary'
        )}<br></h4>`,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        showCancelButton: false,
        confirmButtonColor: '#6576FF',
      });
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
      dispatch(setModal(null));
    }
  };

  return (
    <>
      <UnpauseBeneficiaryHeader handleCloseModal={handleCloseModal} />

      <Modal.Body>
        <form className="form-validate is-alter">
          <UnpauseBeneficiaryDateSection
            pauseStartDate={
              beneficiary?.pause?.startDate
                ? formatDateForInput(beneficiary.pause.startDate)
                : ''
            }
            pauseEndDate={pauseEndDate}
            handleChangePauseEndDate={handleChangePauseEndDate}
          />

          <UnpauseBeneficiaryAllPlansSection
            unPauseAllPlans={unPauseAllPlans}
            handleChangeUnPauseAllPlans={handleChangeUnPauseAllPlans}
          />
        </form>
      </Modal.Body>

      <UnpauseBeneficiaryFooter
        handleUnpauseBeneficiary={handleUnpauseBeneficiary}
      />
      {isLoading && <CustomLoading />}
    </>
  );
};

export default UnpauseBeneficiaryModal;
